import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useAtom } from "jotai";
import { backendDataAtom } from "../../atoms/backendDataAtom.js";
import { useClaimCommunicationActions } from "../../hooks/useClaimCommunicationActions.jsx";
import CommunicationEmailForm from "./CommunicationEmailForm.jsx";
import CommunicationDetailView from "./CommunicationDetailView.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";

function CommunicationModal({
  open,
  onClose,
  kommunikation_id,
  schaden_id,
  canMessageInsurer,
}) {
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [kommunikation, setKommunikation] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();

  const [backendData] = useAtom(backendDataAtom);
  const {
    createCommunication,
    updateCommunication,
    updateCommunicationReminder,
  } = useClaimCommunicationActions();

  const schaden = backendData?.data || {};

  const fetchKommunikation = async ({ silent = false } = {}) => {
    try {
      await backendGet(
        `data/kommunikations/${schaden_id}/${kommunikation_id}`,
        {
          options: {
            params: {
              include: ["createdBy", "anlagen", "outgoingMails", "reminder"],
            },
          },
          setResult: (res) => {
            setKommunikation(res || {});
          },
          // A silent reload keeps the detail view mounted instead of showing the loader.
          setIsLoading: silent ? undefined : setIsLoading,
        }
      );
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Kommunikation", {
        variant: "error",
      });
    }
  };

  // Fetch kommunikation data when modal opens
  useEffect(() => {
    if (open && kommunikation_id) {
      fetchKommunikation();
    } else {
      setKommunikation(null);
    }
    // eslint-disable-next-line
  }, [open, kommunikation_id]);

  const MESSAGE_TYPES = ["insurer_email", "tenant_message", "customer_message"];

  // when a draft is loaded, immediately enter edit mode
  useEffect(() => {
    if (
      MESSAGE_TYPES.includes(kommunikation?.type) &&
      !kommunikation?.sent_at
    ) {
      setEditMode(true);
    }
  }, [kommunikation]);

  // Sync dialog open state and reset editMode when modal opens/closes
  useEffect(() => {
    if (open) {
      setOpenCD(true);
      setEditMode(!kommunikation_id);
    } else {
      setOpenCD(false);
      setEditMode(false);
      setCDActions(null);
    }
    // eslint-disable-next-line
  }, [open, kommunikation_id]);

  // Build tca + CDActions whenever relevant state changes
  useEffect(() => {
    if (!open) return;

    const isNew = !kommunikation_id;
    const isDraft =
      MESSAGE_TYPES.includes(kommunikation?.type) && !kommunikation?.sent_at;

    let canEdit = true;
    if (kommunikation && !isDraft) {
      if (kommunikation.type === "system_message") canEdit = false;
      if (kommunikation.an_versicherer) canEdit = false;
      if (kommunikation.an_weitere?.length > 0) canEdit = false;
    }

    const allDisabled =
      kommunikation &&
      !canMessageInsurer &&
      kommunikation?.created_by?.id !== user.id;

    const canToggleEdit = !isNew && !isDraft && canEdit && !allDisabled;
    const showDraftActions = isNew || isDraft;

    const getTitle = () => {
      if (isNew) {
        return "Neue Mitteilung";
      }
      if (isDraft) {
        return "Mitteilung Entwurf";
      }
      return "Kommunikation Details";
    };

    const getDefaultValues = () => {
      if (isNew) {
        let betreff = schaden.schaden_nummer_accredo + " # ";
        if (schaden.schaden_nummer_vu) {
          betreff = schaden.schaden_nummer_vu + " # " + betreff;
        }
        return {
          schaden_id,
          an_versicherer: false,
          an_weitere: [],
          betreff,
          beschreibung: "",
          anlagen: [],
        };
      }

      if (kommunikation) {
        return {
          ...kommunikation,
          an_weitere: kommunikation.an_weitere || [],
          anlagen: (kommunikation.anlagen || []).map((a) => ({
            id: a.id,
            uuid: a.uuid ?? null,
          })),
          faellig_am: kommunikation.reminder?.faellig_am,
          ist_erledigt: kommunikation.reminder?.ist_erledigt,
        };
      }

      return {};
    };

    const handleSend = async (formData) => {
      const action = isNew ? createCommunication : updateCommunication;

      const payload = isNew
        ? { ...formData, type_category: "message", save_as_draft: false }
        : { ...formData, save_as_draft: false };

      await action(payload, {
        onSuccess: ({ data }) => {
          enqueueSnackbar("Mitteilung gesendet", { variant: "success" });
          updateCommunicationReminder(data.id, formData);
          onClose();
        },
      });
    };

    const handleSaveDraft = async (formData) => {
      const action = isNew ? createCommunication : updateCommunication;

      const payload = isNew
        ? { ...formData, type_category: "message", save_as_draft: true }
        : { ...formData, save_as_draft: true };

      await action(payload, {
        onSuccess: ({ data }) => {
          enqueueSnackbar("Entwurf gespeichert", { variant: "success" });

          updateCommunicationReminder(data.id, formData);

          onClose();
        },
      });
    };

    const handleUpdate = async (formData) => {
      await updateCommunication(formData, {
        onSuccess: () => onClose(),
      });
    };

    const showDetailView =
      !isNew && !isDraft && kommunikation && !isLoading && !editMode;
    const showForm =
      isNew || isDraft || (kommunikation && !isLoading && editMode);

    setTca({
      title: getTitle(),
      maxWidth: "lg",
      content: (
        <>
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Daten werden geladen...</Typography>
            </Box>
          )}
          {showDetailView && (
            <CommunicationDetailView
              key={`${kommunikation_id}-detail`}
              kommunikation={kommunikation}
              kunde={schaden?.kunde}
              outgoingMails={kommunikation.outgoing_mails ?? []}
              onClose={onClose}
              onReload={() => fetchKommunikation({ silent: true })}
            />
          )}
          {showForm && (
            <CommunicationEmailForm
              key={`${kommunikation_id || "new"}-${editMode ? "edit" : "view"}`}
              defaultValues={getDefaultValues()}
              onSubmit={showDraftActions ? handleSend : handleUpdate}
              onSaveDraft={showDraftActions ? handleSaveDraft : undefined}
              onCancel={onClose}
              canMessageInsurer={canMessageInsurer}
              schadenId={schaden_id}
              kommunikationId={kommunikation_id}
              disabled={false}
              showDraftActions={showDraftActions}
              submitLabel={showDraftActions ? "Senden" : "Speichern"}
              submitRequireDirty={!isDraft}
            />
          )}
        </>
      ),
      actions: null,
    });
    // eslint-disable-next-line
  }, [open, kommunikation, isLoading, editMode]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      CDActions={CDActions}
      onClose={onClose}
    />
  );
}

CommunicationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kommunikation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  schaden_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CommunicationModal;
